import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { Customer, DeviceGroup, OnboardingPackage } from "../types";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { formatDateTime } from "../utils/datetime";

type Preflight = {
  agent_exe_present: boolean;
  base_url: string;
  agent_secret_set: boolean;
};

export default function Onboarding() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [groups, setGroups] = useState<DeviceGroup[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [packages, setPackages] = useState<OnboardingPackage[]>([]);
  const [generating, setGenerating] = useState(false);
  const [success, setSuccess] = useState<OnboardingPackage | null>(null);
  const [preflight, setPreflight] = useState<Preflight | null>(null);
  const pkgSort = useSort("created_at", "desc");

  // Option C — deploy via an existing managed agent (PSRemoting fan-out).
  const [deployCustomer, setDeployCustomer] = useState("");
  const [deployTargets, setDeployTargets] = useState("");
  const [deployBusy, setDeployBusy] = useState(false);
  const [deployResult, setDeployResult] = useState<string>("");

  // Top-level tab the page is showing. Local state — no URL persistence
  // for v1 (the tabs are cheap to re-pick on reload).
  const [tab, setTab] = useState<"issue" | "deploy" | "past">("issue");

  const loadPackages = () =>
    api.get("/api/v1/onboarding/list").then((r) => setPackages(r.data));

  useEffect(() => {
    api.get("/api/v1/customers").then((r) => setCustomers(r.data));
    loadPackages();
    api
      .get("/api/v1/onboarding/preflight")
      .then((r) => setPreflight(r.data))
      .catch(() => setPreflight(null));
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      api.get(`/api/v1/customers/${selectedCustomer}/groups`).then((r) => setGroups(r.data));
    } else {
      setGroups([]);
    }
  }, [selectedCustomer]);

  const generate = async () => {
    if (!selectedCustomer) return;
    setGenerating(true);
    setSuccess(null);
    try {
      const { data } = await api.post("/api/v1/onboarding", {
        customer_id: Number(selectedCustomer),
        group_id: selectedGroup ? Number(selectedGroup) : null,
      });
      setSuccess(data);
      loadPackages();
    } finally {
      setGenerating(false);
    }
  };

  const deployViaAgent = async () => {
    if (!deployCustomer || !deployTargets.trim()) return;
    setDeployBusy(true);
    setDeployResult("");
    try {
      const hostnames = deployTargets
        .split(/[\s,;]+/)
        .map((h) => h.trim())
        .filter(Boolean);
      const r = await api.post(`/api/v1/onboarding/deploy-via-agent`, {
        customer_id: Number(deployCustomer),
        target_hostnames: hostnames,
      });
      setDeployResult(
        `Queued install on ${r.data.target_count} target(s) via runner "${r.data.runner_hostname}" (command #${r.data.command_id}). Each target will appear on the dashboard within ~5 minutes.`,
      );
      setDeployTargets("");
    } catch (err: any) {
      setDeployResult(err.response?.data?.detail || "Deploy failed");
    } finally {
      setDeployBusy(false);
    }
  };

  const revoke = async (pkg: OnboardingPackage) => {
    if (
      !window.confirm(
        `Revoke this package? The download link will stop working and anyone who has the ZIP can no longer install new agents from it.`,
      )
    ) {
      return;
    }
    await api.post(`/api/v1/onboarding/${pkg.id}/revoke`);
    if (success?.id === pkg.id) setSuccess(null);
    loadPackages();
  };

  const customerName = (id: number) => customers.find((c) => c.id === id)?.name || `Customer ${id}`;
  const selectedCustomerObj = customers.find((c) => String(c.id) === selectedCustomer);

  // The bootstrap URL is derived from the token; we don't ship it as a
  // separate field on the package because every package gets one.
  const bootstrapOneLiner = (token: string) =>
    `iwr ${window.location.origin}/api/v1/onboarding/bootstrap/${token} | iex`;
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const copyBootstrap = async (token: string) => {
    try {
      await navigator.clipboard?.writeText(bootstrapOneLiner(token));
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      /* clipboard blocked — user can still select the text manually */
    }
  };

  const blockingWarnings: string[] = [];
  if (preflight && !preflight.agent_exe_present) {
    blockingWarnings.push(
      "AeroGauge-Agent.exe isn't staged on the appliance yet — the ZIP will be missing the agent binary and install.bat will fail. The appliance builds it automatically on first boot (and an administrator can rebuild it); wait a minute or two and refresh.",
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Customer Onboarding</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Generate an installer package for a new customer. Run the package on their devices to
        automatically enroll them.
      </p>

      {/* Antivirus / EDR exclusions. The agent is a one-file (PyInstaller)
          build that re-extracts its runtime to a _MEI* Temp folder at launch
          and runs from there — some AV/EDR (e.g. ESET) flag or lock that and
          kill the agent. Surfaced here so techs add the exclusions before
          deploying to a customer's fleet. */}
      <details className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 mb-6 max-w-2xl">
        <summary className="text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer">
          Antivirus / EDR exclusions — add these before installing
        </summary>
        <div className="mt-3 text-xs text-gray-600 dark:text-gray-400 space-y-3">
          <p>
            The agent is a single-file build that re-extracts its runtime to a{" "}
            <code className="font-mono">_MEI*</code> folder in Temp each time it launches and runs
            from there. Some antivirus/EDR (e.g. ESET) flag or lock that behavior and kill the agent.
            Add the exclusions below on the endpoint's AV/EDR (push via policy for a fleet), then
            restart the <code className="font-mono">AeroGaugeRMMAgent</code> service. A{" "}
            <span className="font-semibold">process exclusion is the most reliable</span> — it covers
            the exe and its Temp extraction.
          </p>
          <div>
            <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Process exclusion (preferred)
            </div>
            <ul className="font-mono bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2 space-y-0.5 select-all">
              <li>{"C:\\Program Files\\AeroGauge\\RMMAgent\\AeroGauge-Agent.exe"}</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">
              File / folder exclusions
            </div>
            <ul className="font-mono bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2 space-y-0.5 select-all">
              <li>{"C:\\Program Files\\AeroGauge\\RMMAgent\\*"}</li>
              <li>{"C:\\Windows\\Temp\\_MEI*"}</li>
              <li>{"C:\\Windows\\Temp\\_MEI*\\*"}</li>
            </ul>
          </div>
          <p>
            The agent runs as the SYSTEM service <code className="font-mono">AeroGaugeRMMAgent</code>,
            so its <code className="font-mono">_MEI*</code> extraction lives in{" "}
            <code className="font-mono">C:\Windows\Temp</code>. If an endpoint runs the agent under a
            normal user instead, also exclude{" "}
            <code className="font-mono">{"C:\\Users\\*\\AppData\\Local\\Temp\\_MEI*\\*"}</code>. If AV
            already quarantined <code className="font-mono">AeroGauge-Agent.exe</code>, restore it,
            add the exclusions, then reboot.
          </p>
        </div>
      </details>

      {blockingWarnings.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-6 max-w-2xl">
          <div className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">
            Heads up — platform not fully ready
          </div>
          <ul className="text-xs text-amber-800 dark:text-amber-300 list-disc ml-5 space-y-1">
            {blockingWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {customers.length === 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-xl p-4 mb-6 max-w-2xl">
          <p className="text-sm text-blue-900 dark:text-blue-200">
            No customers yet —{" "}
            <Link to="/customers" className="font-semibold underline">
              create one first
            </Link>
            , then come back here to issue an installer package.
          </p>
        </div>
      )}

      {/* Tabs — keeps the page from sprawling into a single tall scroll. */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6 -mt-2 overflow-x-auto">
        {[
          { key: "issue", label: "Issue install package" },
          { key: "deploy", label: "Mass deploy via existing agent" },
          { key: "past", label: `Past packages (${packages.length})` },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key as "issue" | "deploy" | "past")}
            className={
              "px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors " +
              (tab === t.key
                ? "border-blue-600 text-blue-700 dark:text-blue-400 font-semibold"
                : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className={tab === "issue" ? "" : "hidden"}>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 mb-6 max-w-lg">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Generate New Installer Package</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Customer <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedCustomer}
              onChange={(e) => {
                setSelectedCustomer(e.target.value);
                setSelectedGroup("");
              }}
              disabled={customers.length === 0}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-400"
            >
              <option value="">Select a customer...</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {groups.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Device Group (optional)
              </label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Default Group</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={generate}
            disabled={!selectedCustomer || generating}
            className="w-full bg-blue-600 text-white text-sm py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {generating ? "Generating..." : "Generate Installer Package"}
          </button>
        </div>

        {success && (
          <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-700">
            <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">Package ready!</p>

            <div className="text-xs text-green-900 dark:text-green-200 mb-3">
              Two ways to install. Pick whichever fits the customer's environment — both produce
              the same managed device on the dashboard within ~5 minutes.
            </div>

            <div className="mb-3 p-3 bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Option A — One-liner remote install (recommended for fleets)
              </div>
              <p className="text-[11px] text-gray-600 dark:text-gray-400 mb-2">
                Run this from an <strong>Administrator PowerShell</strong> on each target
                machine. Wrap it in a GPO Computer Startup script, an Intune Win32 app, or
                <code className="mx-1 bg-gray-100 dark:bg-gray-700 px-1 rounded">Invoke-Command -ComputerName $list</code>{" "}
                from a domain controller to fan it out.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-900 text-green-300 text-[11px] px-2 py-1.5 rounded font-mono break-all">
                  {bootstrapOneLiner(success.token)}
                </code>
                <button
                  onClick={() => copyBootstrap(success.token)}
                  className="text-xs border border-gray-300 dark:border-gray-600 dark:text-gray-200 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  {copiedToken === success.token ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            <div className="mb-3 p-3 bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Option B — Hand-install via ZIP
              </div>
              <p className="text-[11px] text-gray-600 dark:text-gray-400 mb-2">
                Download the ZIP, copy the folder to the device, right-click{" "}
                <strong>install.bat</strong> → "Run as Administrator". Best for one-offs or
                machines without internet during install.
              </p>
              <a
                href={success.download_url}
                className="inline-block bg-green-600 text-white text-xs px-3 py-1.5 rounded hover:bg-green-700"
              >
                Download ZIP
              </a>
            </div>

            <p className="text-xs text-green-800 dark:text-green-300 mb-2">
              After install, watch{" "}
              <Link to={`/devices?customer_id=${success.customer_id}`} className="underline">
                Devices → {selectedCustomerObj?.name || customerName(success.customer_id)}
              </Link>{" "}
              — devices appear within ~5 minutes of their first checkin.
            </p>

            {isAdmin && (
              <button
                onClick={() => revoke(success)}
                className="text-xs text-red-700 dark:text-red-400 hover:underline"
              >
                Revoke this package's token
              </button>
            )}
          </div>
        )}
      </div>
      </div>

      <div className={tab === "deploy" ? "" : "hidden"}>
      {isAdmin ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 mb-6 max-w-2xl">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
            Deploy via existing managed agent
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            For customers that already have a managed device marked as <strong>AD runner</strong>,
            push the agent to additional hostnames via PowerShell Remoting. The runner needs to
            be online and running its agent service under a domain admin account (LocalSystem
            can't PSRemote against peers).
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer</label>
              <select
                value={deployCustomer}
                onChange={(e) => setDeployCustomer(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select a customer...</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Target hostnames
              </label>
              <textarea
                value={deployTargets}
                onChange={(e) => setDeployTargets(e.target.value)}
                rows={3}
                placeholder="PC-001, PC-002, SRV-FILE01   (comma, space, or newline separated)"
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
              />
            </div>
            <button
              onClick={deployViaAgent}
              disabled={!deployCustomer || !deployTargets.trim() || deployBusy}
              className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {deployBusy ? "Queueing..." : "Push install to listed hosts"}
            </button>
            {deployResult && (
              <div
                className={
                  "text-xs rounded p-3 border " +
                  (deployResult.startsWith("Queued")
                    ? "bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700 text-green-800 dark:text-green-300"
                    : "bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300")
                }
              >
                {deployResult}
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">Admin role required to push agent installs via an existing runner.</p>
      )}
      </div>

      <div className={tab === "past" ? "" : "hidden"}>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">All Onboarding Packages</h2>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="Customer" sortKey="customer" sort={pkgSort.sort} onSort={pkgSort.toggle} />
              <SortableTh label="Created" sortKey="created_at" sort={pkgSort.sort} onSort={pkgSort.toggle} />
              <SortableTh label="Downloads" sortKey="download_count" sort={pkgSort.sort} onSort={pkgSort.toggle} />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortRows(packages, pkgSort.sort, (p, key) => {
              switch (key) {
                case "created_at": return p.created_at ? Date.parse(p.created_at) : 0;
                case "download_count": return p.download_count;
                default: return customerName(p.customer_id).toLowerCase();
              }
            }).map((p) => (
              <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className="px-4 py-3 font-medium dark:text-gray-100">{customerName(p.customer_id)}</td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                  {formatDateTime(p.created_at)}
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{p.download_count}</td>
                <td className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <a
                    href={p.download_url}
                    className="text-blue-600 dark:text-blue-400 hover:underline text-xs font-medium"
                  >
                    Download ZIP
                  </a>
                  <button
                    onClick={() => copyBootstrap(p.token)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    title="Copy PowerShell one-liner for remote install"
                  >
                    {copiedToken === p.token ? "Copied" : "Copy install one-liner"}
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => revoke(p)}
                      className="text-xs text-red-600 dark:text-red-400 hover:underline"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {packages.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                  No packages generated yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}
